import { Alert, Button, Input } from "antd";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { LogoWhite } from "../../../assets/svg/Svgs";
import { APP_NAME, LOGIN } from "../../../constants/constants";
import {
  API_OTP_REQUEST,
  API_OTP_VERIFY_SIGNUP,
} from "../../../constants/apis";
import useSet from "../../../hooks/common/useSet";
import { currentYear } from "../../../utils/helpers";

/**
 * Signup phone-verification gate.
 * The signup flow redirects here with ?phone=+2567XXX after creating the user.
 * The backend automatically sends the first OTP on user creation, but we offer
 * a "resend" button here that hits /auth/otp/request/ with purpose=signup_verify.
 *
 * Note: backend currently only accepts purpose=login|password_reset on the public
 * request endpoint, so "resend" here calls the same endpoint with purpose=login
 * (which won't help signups). Wire a dedicated resend endpoint later if needed.
 */
export const OtpSignupVerifyPage = () => {
  const [params] = useSearchParams();
  const [phone] = useState(params.get("phone") || "");
  const [code, setCode] = useState("");
  const [done, setDone] = useState(false);
  const { uploadData, error, submitting } = useSet();
  const navigate = useNavigate();

  const verify = (e: any) => {
    e.preventDefault();
    if (!code) return;
    uploadData({
      api: API_OTP_VERIFY_SIGNUP,
      params: { phone_number: phone, code },
      onSuccess: () => setDone(true),
      successMessage: "Phone verified. You can now log in.",
    });
  };

  const resend = () => {
    uploadData({
      api: API_OTP_REQUEST,
      params: { phone_number: phone, purpose: "login" },
      successMessage: "A new code has been sent if your phone is registered.",
    });
  };

  const errorMessage = (() => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (error?.message) return error.message;
    if (error?.non_field_errors)
      return Array.isArray(error.non_field_errors)
        ? error.non_field_errors[0]
        : error.non_field_errors;
    if (error?.code) return Array.isArray(error.code) ? error.code[0] : error.code;
    if (typeof error === "object") {
      const k = Object.keys(error)[0];
      const v = error[k];
      return Array.isArray(v) ? v[0] : v;
    }
    return "An error occurred";
  })();

  return (
    <Wrapper className="vh100 bgGradient white flexColumn">
      <img id="overlay" alt={APP_NAME} src={LogoWhite} />
      <div className="flexGrow container flexCenter">
        <FormWrapper className="r15 whiteBg black flexColumn gap20">
          <div className="flexNullCenter gap15">
            <img
              alt={APP_NAME}
              src={LogoWhite}
              width={40}
              height={40}
              style={{ filter: "brightness(2000%)" }}
            />
            <div className="extraBold" style={{ fontSize: 25 }}>
              everpesa
            </div>
          </div>

          <div className="extraBold textDark" style={{ fontSize: 22 }}>
            {done ? "Phone Verified" : "Verify your phone"}
          </div>

          <div className="light font13">
            {done
              ? "Your account is now active. Please log in to continue."
              : `We sent a 6-digit code to ${phone || "your phone"}. Enter it below.`}
          </div>

          {!done && (
            <form onSubmit={verify} className="flexColumn gap15">
              <Input
                size="large"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={8}
                autoFocus
              />
              {errorMessage && (
                <Alert className="font12" message={errorMessage} type="error" showIcon />
              )}
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                block
                className="capitalize noShadow r7"
              >
                Verify
              </Button>
              <Button type="link" onClick={resend} block>
                Resend code
              </Button>
            </form>
          )}

          {done && (
            <Button
              type="primary"
              size="large"
              block
              onClick={() => navigate(`/auth/${LOGIN}`)}
            >
              Continue to Login
            </Button>
          )}
        </FormWrapper>
      </div>

      <div className="font12 textCenter" style={{ padding: 30 }}>
        © {currentYear()} {APP_NAME}. All rights reserved.
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  padding: 40px 0;
  #overlay {
    opacity: 0.1;
    position: absolute;
    left: -10%;
    bottom: -10%;
    width: 700px;
    pointer-events: none;
  }
`;

const FormWrapper = styled.div`
  max-width: 460px;
  width: 100%;
  padding: 30px;
  margin: 40px auto;
`;
